import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/concepts/data-integration')({
  component: DataIntegrationPage,
  head: () => ({
    meta: [
      { title: 'Data Integration | Matricks' },
      { name: 'description', content: 'Learn about data integration strategies, patterns, and best practices. Complete guide to connecting and synchronizing data across systems.' },
    ],
  }),
});

const benefits = [
  {
    iconName: "Database",
    title: "Unified Data View",
    description: "Combine data from multiple sources into a single, consistent view for analysis and operations.",
  },
  {
    iconName: "RefreshCw",
    title: "Real-Time Sync",
    description: "Keep data synchronized across systems automatically, eliminating manual updates and stale data.",
  },
  {
    iconName: "Zap",
    title: "Automated Workflows",
    description: "Trigger actions when data changes—update CRM when orders are placed, sync inventory across channels.",
  },
  {
    iconName: "Shield",
    title: "Data Quality",
    description: "Validate, cleanse, and transform data as it moves, ensuring consistency and accuracy.",
  },
  {
    iconName: "Clock",
    title: "Faster Decisions",
    description: "Access integrated data in real-time for faster, more informed business decisions.",
  },
  {
    iconName: "BarChart3",
    title: "Better Analytics",
    description: "Combine data from multiple sources for comprehensive analytics and reporting.",
  },
];

const patterns = [
  {
    title: "ETL (Extract, Transform, Load)",
    description: "Extract data from sources, transform it to fit the target schema, then load it into the destination. Best for batch processing and data warehousing.",
    pros: ["Well-established pattern", "Good for large data volumes", "Transformation before loading"],
    cons: ["Batch-oriented, not real-time", "Complex transformation logic", "Latency between source and target"],
  },
  {
    title: "ELT (Extract, Load, Transform)",
    description: "Load raw data first, then transform it in the destination. Leverages the processing power of modern data warehouses.",
    pros: ["Faster initial loading", "Flexible transformations", "Raw data preserved"],
    cons: ["Requires powerful destination", "Raw data storage costs", "Transform logic in destination"],
  },
  {
    title: "Change Data Capture (CDC)",
    description: "Capture and propagate changes as they happen. Enables real-time data synchronization with minimal impact on source systems.",
    pros: ["Real-time updates", "Low source impact", "Incremental processing"],
    cons: ["Complex setup", "Source system support needed", "Order preservation challenges"],
  },
  {
    title: "API-Based Integration",
    description: "Connect systems through their APIs. Flexible and works with any API-enabled application.",
    pros: ["Universal compatibility", "Real-time capable", "No database access needed"],
    cons: ["Rate limits", "API versioning", "Pagination handling"],
  },
];

const faqItems = [
  {
    question: "What's the difference between data integration and data migration?",
    answer: "Data migration is a one-time move of data from one system to another. Data integration is an ongoing process that keeps data synchronized across systems continuously. Migration is a project; integration is an operational capability.",
  },
  {
    question: "How do I handle conflicting data from multiple sources?",
    answer: "Conflict resolution strategies include: last-write-wins (most recent update prevails), source priority (certain systems are authoritative), merge strategies (combine non-conflicting fields), or flagging for manual review when conflicts can't be automatically resolved.",
  },
  {
    question: "Should I use batch or real-time integration?",
    answer: "It depends on your requirements. Real-time is essential when data freshness matters (inventory, pricing, fraud detection). Batch is often sufficient for analytics, reporting, or when processing large volumes where latency is acceptable.",
  },
  {
    question: "How do I ensure data quality in integrations?",
    answer: "Implement validation rules at ingestion, use data profiling to understand source quality, apply transformations to standardize formats, implement error handling for bad data, and monitor data quality metrics over time.",
  },
  {
    question: "What about data governance in integrations?",
    answer: "Data governance should cover: data lineage (where data comes from), access controls (who can access what), data classification (sensitivity levels), retention policies, and compliance requirements (GDPR, HIPAA). Build governance into your integration architecture.",
  },
];

function DataIntegrationPage() {
  return (
    <div>
      <ContentHeader
        title="Data Integration"
        description="Data integration combines data from different sources to provide users with a unified, consistent view. It's essential for breaking down data silos and enabling data-driven decisions."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Concepts", href: "/concepts" },
          { label: "Data Integration" },
        ]}
        badge="Concept"
      />

      {/* What is Data Integration */}
      <ContentSection id="what-is-data-integration" title="Understanding Data Integration">
        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p className="text-lg text-muted-foreground leading-relaxed">
            Modern organizations use dozens or hundreds of different applications—CRM, ERP, marketing
            automation, support systems, databases, and more. Each holds valuable data, but in isolation,
            that data's value is limited.
          </p>

          <p className="text-muted-foreground leading-relaxed mt-4">
            Data integration connects these systems, allowing data to flow between them automatically.
            When a customer updates their address in your CRM, that change can propagate to your
            billing system, shipping system, and marketing platform—all without manual intervention.
          </p>
        </div>
      </ContentSection>

      {/* Benefits */}
      <ContentSection
        id="benefits"
        title="Key Benefits"
        description="Why data integration is essential for modern organizations."
        background="muted"
      >
        <FeatureShowcase features={benefits} columns={3} variant="cards" />
      </ContentSection>

      {/* Patterns */}
      <ContentSection
        id="patterns"
        title="Integration Patterns"
        description="Common approaches to data integration, each with different trade-offs."
      >
        <div className="space-y-6">
          {patterns.map((pattern) => (
            <div key={pattern.title} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg">{pattern.title}</h3>
              <p className="text-muted-foreground mt-2">{pattern.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <h4 className="text-sm font-medium text-green-600 dark:text-green-400 mb-2">Pros</h4>
                  <ul className="space-y-1">
                    {pattern.pros.map((pro, i) => (
                      <li key={i} className="text-sm text-muted-foreground">• {pro}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">Cons</h4>
                  <ul className="space-y-1">
                    {pattern.cons.map((con, i) => (
                      <li key={i} className="text-sm text-muted-foreground">• {con}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Best Practices */}
      <ContentSection
        id="best-practices"
        title="Best Practices"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Start with Business Requirements</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Understand what data needs to flow where and why. Let business requirements
              drive technical decisions, not the other way around.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Design for Scalability</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Data volumes grow. Design integrations that can handle increased load without
              complete redesign. Consider horizontal scaling from the start.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Implement Error Handling</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Failures happen. Build robust error handling with retries, dead letter queues,
              and alerting. Make integrations self-healing where possible.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Monitor and Measure</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Track data volumes, latency, error rates, and data quality metrics. Set up
              alerts for anomalies and SLA violations.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Related Concepts */}
      <ContentSection id="related" title="Related Concepts">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/concepts/api-gateway"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">API Gateway</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Unified access to data sources
            </p>
          </Link>
          <Link
            to="/concepts/webhooks"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Webhooks</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Real-time event-driven integration
            </p>
          </Link>
          <Link
            to="/features/integrations"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">2000+ Integrations</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Pre-built connectors in Matricks
            </p>
          </Link>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Simplify Your Data Integration"
        description="Matricks provides 2000+ pre-built integrations and a visual workflow builder to connect your data across any system."
        primaryAction={{ label: "Get Started", href: "/login" }}
        secondaryAction={{ label: "View Integrations", href: "/features/integrations" }}
        background="primary"
      />
    </div>
  );
}
